/**
 * Sentry React initialization (#147 — Phase 2).
 *
 * Mirrors backend Phase 1 (#146) — same scrubbing posture, same release
 * tagging, single project on Sentry side (`launcher-webapp` under
 * `vrx-gaming-inc`), distinguished staging↔production via the
 * `environment` tag from Vite's MODE.
 *
 * Imported on line 1 of `main.tsx`. Initializing before React's render
 * means errors during initial mount (auth init, etc — the #145 class of
 * blank-screen bug) get captured with full context.
 *
 * No-op in `development` mode so `npm run dev` doesn't ping Sentry.
 */

import * as Sentry from '@sentry/react';

// DSN is browser-side and shows up in every event's network request — it
// is public by design (Sentry's docs are explicit about this). Hardcode
// rather than wire VITE_* env injection just for a public string.
const DSN =
  'https://e8f0447a53b96e13fba0667d4d3e32f8@o4511500541296640.ingest.us.sentry.io/4511529617063936';

const mode = import.meta.env.MODE;
const enabled = mode === 'staging' || mode === 'production';

if (enabled) {
  Sentry.init({
    dsn: DSN,
    environment: mode,
    // Release tag wired via VITE_GIT_COMMIT injected at CI build time
    // (see .gitlab-ci.yml — set in the same export GIT_COMMIT block that
    // populates the backend's release). Matches backend tag exactly, so
    // a single SHA links errors across both surfaces.
    release: (import.meta.env.VITE_GIT_COMMIT as string | undefined) || undefined,

    // #152 — broad safety net for caught-and-logged errors.
    // Many of our catch blocks end in `console.error('❌ ...', err)` and
    // never re-throw (e.g. AuthContext.initialize, AuthContext.logout
    // backend-invalidation catch, etc.). Sentry's default integrations
    // only capture *unhandled* exceptions, so those catch-and-log spots
    // are invisible to Sentry. This integration mirrors every
    // `console.error(...)` call into Sentry as a captured event.
    //
    // We deliberately only opt in to the `error` level — `console.log`
    // and `console.warn` would be noisy and uninteresting.
    //
    // Targeted `Sentry.captureException()` calls in the auth catches
    // still take precedence — they ship structured `tags`/`extra`
    // metadata this integration can't infer. This is the floor, not the
    // ceiling.
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],

    sendDefaultPii: false,

    // 10% performance tracing in prod; full in staging. /api/auth/guest
    // fires on every page load so this is the cost lever.
    tracesSampleRate: mode === 'production' ? 0.1 : 1.0,

    // Distributed tracing — propagate trace headers ONLY to our own
    // backend so we get end-to-end traces stitching webapp → Express.
    // Never propagate to third parties (Patreon, R2, CDN).
    tracePropagationTargets: [
      'launcher.adultvrgames.net',
      'launcher-staging.adultvrgames.net',
      /^\/api\//,
    ],

    // Session Replay deliberately OFF — adult-content product. Replay
    // would capture DOM state visually. Revisit only with explicit
    // masking config review.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Scrub sensitive data before any event leaves the browser.
    beforeSend(event) {
      try {
        if (event.request) {
          // Scrub URL query params
          if (event.request.url) {
            try {
              const u = new URL(event.request.url);
              for (const k of Array.from(u.searchParams.keys())) {
                if (/^(token|code|state|access_token|refresh_token|api_key|key)$/i.test(k)) {
                  u.searchParams.set(k, '[REDACTED]');
                }
              }
              event.request.url = u.toString();
            } catch {
              /* relative or invalid URL — leave as-is */
            }
          }
          // Scrub headers (browser SDK usually doesn't populate these but be safe)
          if (event.request.headers) {
            for (const k of Object.keys(event.request.headers)) {
              if (/^(authorization|cookie|x-device-uuid|x-patreon-.*)$/i.test(k)) {
                event.request.headers[k] = '[REDACTED]';
              }
            }
          }
        }

        // Scrub fetch/xhr breadcrumb data
        if (event.breadcrumbs) {
          for (const bc of event.breadcrumbs) {
            if (bc.category === 'fetch' || bc.category === 'xhr') {
              if (bc.data?.url && typeof bc.data.url === 'string') {
                try {
                  const u = new URL(bc.data.url, window.location.origin);
                  for (const k of Array.from(u.searchParams.keys())) {
                    if (/^(token|code|state|access_token|refresh_token|api_key|key)$/i.test(k)) {
                      u.searchParams.set(k, '[REDACTED]');
                    }
                  }
                  bc.data.url = u.toString();
                } catch {
                  /* ignore */
                }
              }
            }
          }
        }

        // Defensively delete user PII fields
        if (event.user) {
          delete event.user.email;
          delete event.user.ip_address;
          delete event.user.username;
        }
      } catch {
        // Fail closed — drop the event rather than risk leaking unscrubbed data.
        return null;
      }
      return event;
    },

    maxBreadcrumbs: 50,
  });

  // Friendly boot log so we can confirm initialization in DevTools.
  // eslint-disable-next-line no-console
  console.log(
    `🛰️  Sentry initialized (env=${mode}, release=${(import.meta.env.VITE_GIT_COMMIT as string | undefined)?.substring(0, 7) || 'unknown'})`
  );
} else {
  // eslint-disable-next-line no-console
  console.log(`ℹ️  Sentry disabled (mode=${mode})`);
}
